import { useFieldState } from 'informed';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ProgressBar } from '../progressBars/LinearProgressBar';

import { ProgressBarMode } from '../progressBars/LinearProgressBar/styled';

interface PasswordStrengthCheckerProps {
  name: string;
}

const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 30;

const BAR_OFFSET_PX = 80;
const BAR_FULL = 100;
const BAR_EMPTY = 0;

export const PasswordStrengthChecker: FC<PasswordStrengthCheckerProps> = ({
  name,
}) => {
  const { t } = useTranslation('validation');

  const password = useFieldState<string | undefined>(name);

  const { isShort, isLong } = useMemo(() => {
    const length = password.value?.length;
    return {
      isShort: length < MIN_PASSWORD_LENGTH,
      isLong: length > MAX_PASSWORD_LENGTH,
    };
  }, [password]);

  const isValidLength = !(isShort || isLong);

  return (
    <ScPasswordStrengthChecker className="password-strength-checker">
      <ProgressBar
        borderRadius="4px"
        height={4}
        mode={isValidLength ? ProgressBarMode.SUCCESS : ProgressBarMode.ERROR}
        padding={0}
        value={isValidLength ? BAR_FULL : BAR_EMPTY}
        valuePx={isValidLength ? BAR_EMPTY : BAR_OFFSET_PX}
      />
      <ScStrengthItem isActive={isValidLength}>
        {isValidLength
          ? t('strongPassword')
          : isLong
            ? t('invalidPasswordLong')
            : t('invalidPasswordShort')}
      </ScStrengthItem>
    </ScPasswordStrengthChecker>
  );
};

const ScPasswordStrengthChecker = styled.ul`
  margin: 4px 16px 0 16px;
  position: relative;
  left: 0;
  right: 0;
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 8px;
  grid-row-gap: 4px;
  border-radius: 24px;
  z-index: 1;
`;

interface ScStrengthItemProps {
  isActive: boolean;
}

const ScStrengthItem = styled.div<ScStrengthItemProps>`
  display: block;
  position: relative;
  font-size: 12px;
  line-height: 18px;
  color: ${({ isActive, theme }) => {
    if (isActive) {
      return theme.greenLightColor;
    }
    return theme.redColor;
  }};
`;
